import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Search, Pencil } from 'lucide-react';
import { useUser } from 'context/user/User';
import establishmentsApi from 'api/requests/establishments';
import ResponsiveModal from 'components/_UI/ResponsiveModal';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import { panelAccent, panelAccentAlpha } from 'theme/panelAccent';

function formatDate(dateStr) {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('pt-BR');
}

function formatPrice(value) {
    return Number(value || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

function statusLabel(status) {
    const map = { open: 'Aberta', closed: 'Fechada', cancelled: 'Cancelada', unpaid: "Pendente de pagamento" };
    return map[status] || status;
}

function statusColor(status) {
    if (status === 'open') return colors.occasionalGreen;
    if (status === 'cancelled') return colors.warning;
    if (status === 'unpaid') return colors.danger;
    return colors.nightLight;
}

export default function CustomersPage() {
    const { user } = useUser();
    const slug = user?.establishments?.[0]?.slug;

    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState('');
    const debounceRef = useRef(null);

    // Edit sheet
    const [editTarget, setEditTarget] = useState(null);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [editPhone, setEditPhone] = useState('');
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);

    async function fetchCustomers(searchValue) {
        if (!slug) return;
        setLoading(true);
        setError(null);
        try {
            const params = searchValue ? { search: searchValue } : {};
            const { data } = await establishmentsApi.getCustomers(slug, params);
            setCustomers(data?.data || []);
        } catch (err) {
            setError(err?.response?.data?.message || 'Erro ao carregar clientes.');
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchCustomers('');
    }, [slug]);

    function handleSearchChange(e) {
        const value = e.target.value.slice(0, 100);
        setSearch(value);
        clearTimeout(debounceRef.current);
        debounceRef.current = setTimeout(() => fetchCustomers(value), 400);
    }

    function openEdit(customer) {
        setEditTarget(customer);
        setEditName(customer.person_name || '');
        setEditEmail(customer.email || '');
        setEditPhone(customer.phone || '');
        setSaveError(null);
    }

    function closeEdit() {
        setEditTarget(null);
        setSaveError(null);
    }

    async function handleSave() {
        if (!editTarget || !slug) return;
        setSaving(true);
        setSaveError(null);
        try {
            const payload = {};
            if (editName !== editTarget.person_name) payload.person_name = editName;
            if (editEmail !== editTarget.email) payload.email = editEmail;
            if (editPhone !== editTarget.phone) payload.phone = editPhone;

            const { data } = await establishmentsApi.updateCustomer(slug, editTarget.id, payload);

            setCustomers(prev => prev.map(c => c.id === data.id ? data : c));
            closeEdit();
        } catch (err) {
            setSaveError(err?.response?.data?.message || 'Erro ao salvar alterações.');
        } finally {
            setSaving(false);
        }
    }

    return (
        <Page>
            <Section>
                <SectionHeader>
                    <SectionTitle>Clientes</SectionTitle>
                    {!loading && !error && (
                        <CustomerCount>{customers.length} encontrado{customers.length !== 1 ? 's' : ''}</CustomerCount>
                    )}
                </SectionHeader>

                <SearchWrapper>
                    <SearchIcon>
                        <Search size={16} color={colors.nightLight} />
                    </SearchIcon>
                    <SearchInput
                        type="text"
                        placeholder="Buscar por nome, CPF ou e-mail..."
                        value={search}
                        onChange={handleSearchChange}
                        maxLength={100}
                    />
                </SearchWrapper>

                {loading && <StateText>Carregando...</StateText>}

                {!loading && error && <StateText $error>{error}</StateText>}

                {!loading && !error && customers.length === 0 && (
                    <EmptyState>
                        <EmptyTitle>Nenhum cliente encontrado</EmptyTitle>
                        <EmptyHint>
                            {search
                                ? 'Tente buscar por outro nome, CPF ou e-mail.'
                                : 'Os clientes aparecerão aqui após realizarem pedidos.'}
                        </EmptyHint>
                    </EmptyState>
                )}

                {!loading && !error && customers.length > 0 && (
                    <TableWrapper>
                        <Table>
                            <thead>
                                <tr>
                                    <Th>Nome</Th>
                                    <Th $hideOnMobile>CPF</Th>
                                    <Th $hideOnMobile>E-mail</Th>
                                    <Th $hideOnMobile>Telefone</Th>
                                    <Th $hideOnMobile>Desde</Th>
                                    <Th $hideOnMobile>Comandas</Th>
                                    <Th />
                                </tr>
                            </thead>
                            <tbody>
                                {customers.map(customer => (
                                    <tr key={customer.id}>
                                        <Td>
                                            <CustomerName>{customer.person_name}</CustomerName>
                                            <MobileDetails>
                                                {customer.cpf && <span>{customer.cpf}</span>}
                                                {customer.email && <span>{customer.email}</span>}
                                                {customer.phone && <span>{customer.phone}</span>}
                                                {customer.commands?.length > 0 && (
                                                    <span>{customer.commands.length} comanda{customer.commands.length !== 1 ? 's' : ''}</span>
                                                )}
                                            </MobileDetails>
                                        </Td>
                                        <Td $hideOnMobile>{customer.cpf || '—'}</Td>
                                        <Td $hideOnMobile>{customer.email || '—'}</Td>
                                        <Td $hideOnMobile>{customer.phone || '—'}</Td>
                                        <Td $hideOnMobile>{formatDate(customer.created_at)}</Td>
                                        <Td $hideOnMobile>
                                            {customer.commands?.length > 0
                                                ? <CommandCount>{customer.commands.length}</CommandCount>
                                                : <span style={{ color: colors.nightLight }}>—</span>
                                            }
                                        </Td>
                                        <Td>
                                            <EditBtn onClick={() => openEdit(customer)} title="Editar cliente">
                                                <Pencil size={15} />
                                            </EditBtn>
                                        </Td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </TableWrapper>
                )}
            </Section>

            {/* ── Edit sheet ── */}
            <ResponsiveModal
                isOpen={!!editTarget}
                onClose={closeEdit}
                title={editTarget?.person_name}
                size="large"
            >
                {editTarget && (
                    <SheetBody>
                        {/* ── Edit form ── */}
                        <SheetSection>
                            <SheetSectionTitle>Dados do cliente</SheetSectionTitle>

                            <Field>
                                <FieldLabel>Nome</FieldLabel>
                                <FieldInput
                                    value={editName}
                                    onChange={e => setEditName(e.target.value)}
                                    placeholder="Nome completo"
                                />
                            </Field>

                            <Field>
                                <FieldLabel>CPF</FieldLabel>
                                <FieldInput value={editTarget.cpf || ''} disabled />
                            </Field>

                            <Field>
                                <FieldLabel>E-mail</FieldLabel>
                                <FieldInput
                                    type="email"
                                    value={editEmail}
                                    onChange={e => setEditEmail(e.target.value)}
                                    placeholder="email@exemplo.com"
                                />
                            </Field>

                            <Field>
                                <FieldLabel>Telefone</FieldLabel>
                                <FieldInput
                                    type="tel"
                                    value={editPhone}
                                    onChange={e => setEditPhone(e.target.value)}
                                    placeholder="11999999999"
                                />
                            </Field>

                            {saveError && <ErrorMsg>{saveError}</ErrorMsg>}

                            <SaveBtn onClick={handleSave} disabled={saving}>
                                {saving ? 'Salvando...' : 'Salvar alterações'}
                            </SaveBtn>
                        </SheetSection>

                        {/* ── Commands list ── */}
                        {editTarget.commands?.length > 0 && (
                            <SheetSection>
                                <SheetSectionTitle>
                                    Comandas
                                    <CommandBadge>{editTarget.commands.length}</CommandBadge>
                                </SheetSectionTitle>

                                <CommandList>
                                    {editTarget.commands.map(cmd => (
                                        <CommandRow key={cmd.id}>
                                            <CommandInfo>
                                                <CommandId>#{cmd.id}</CommandId>
                                                {cmd.table_identifier && (
                                                    <CommandTable>Mesa {cmd.table_identifier}</CommandTable>
                                                )}
                                                <CommandDate>{formatDate(cmd.created_at)}</CommandDate>
                                            </CommandInfo>
                                            <CommandMeta>
                                                {cmd.total != null && (
                                                    <CommandTotal>{formatPrice(cmd.total)}</CommandTotal>
                                                )}
                                                <StatusBadge $color={statusColor(cmd.status)}>
                                                    {statusLabel(cmd.status)}
                                                </StatusBadge>
                                            </CommandMeta>
                                        </CommandRow>
                                    ))}
                                </CommandList>
                            </SheetSection>
                        )}

                        {editTarget.commands?.length === 0 && (
                            <SheetSection>
                                <SheetSectionTitle>Comandas</SheetSectionTitle>
                                <NoCommandsHint>Nenhuma comanda registrada para este cliente.</NoCommandsHint>
                            </SheetSection>
                        )}
                    </SheetBody>
                )}
            </ResponsiveModal>
        </Page>
    );
}

/* ─── Layout ─── */

const Page = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const Section = styled.div`
    background: #fff;
    border-radius: 14px;
    border: 1px solid ${colors.neutralDark};
    overflow: hidden;
`;

const SectionHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SectionTitle = styled.h3`
    font-size: 16px;
    font-weight: 600;
    color: ${colors.nightDark};
    margin: 0;
`;

const CustomerCount = styled.span`
    font-size: 13px;
    color: ${colors.nightLight};
    font-weight: 500;
`;

/* ─── Search ─── */

const SearchWrapper = styled.div`
    position: relative;
    padding: 14px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
`;

const SearchIcon = styled.div`
    position: absolute;
    left: 34px;
    top: 50%;
    transform: translateY(-50%);
    pointer-events: none;
    display: flex;
    align-items: center;
`;

const SearchInput = styled.input`
    width: 100%;
    box-sizing: border-box;
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 11px 16px 11px 38px;
    font-size: 14px;
    color: ${colors.nightDark};
    background: ${colors.neutralBackground};
    outline: none;
    transition: border-color 0.15s;

    &:focus {
        border-color: ${panelAccent(colors.occasionalPurple)};
        background: #fff;
    }

    &::placeholder {
        color: ${colors.nightLight};
    }
`;

/* ─── States ─── */

const StateText = styled.p`
    text-align: center;
    color: ${({ $error }) => $error ? colors.danger : colors.nightLight};
    font-size: 14px;
    padding: 40px;
    margin: 0;
`;

const EmptyState = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px 24px;
    text-align: center;
    gap: 8px;
`;

const EmptyTitle = styled.p`
    font-weight: 600;
    font-size: 15px;
    color: ${colors.nightDark};
    margin: 0;
`;

const EmptyHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 0;
`;

/* ─── Table ─── */

const TableWrapper = styled.div`
    overflow-x: auto;
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;
`;

const Th = styled.th`
    text-align: left;
    font-size: 12px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    padding: 12px 20px;
    border-bottom: 1px solid ${colors.neutralDark};
    background: ${colors.neutralBackground};
    white-space: nowrap;

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const Td = styled.td`
    padding: 14px 20px;
    font-size: 14px;
    color: ${colors.nightDark};
    border-bottom: 1px solid ${colors.neutralDark};
    vertical-align: middle;

    tr:last-child & {
        border-bottom: none;
    }

    ${({ $hideOnMobile }) => $hideOnMobile && `
        @media screen and (max-width: ${breakpoints.md}) {
            display: none;
        }
    `}
`;

const CustomerName = styled.span`
    font-weight: 600;
    display: block;
`;

const MobileDetails = styled.div`
    display: none;
    flex-direction: column;
    gap: 2px;
    margin-top: 4px;

    & > span {
        font-size: 12px;
        color: ${colors.nightLight};
    }

    @media screen and (max-width: ${breakpoints.md}) {
        display: flex;
    }
`;

const CommandCount = styled.span`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 22px;
    height: 22px;
    padding: 0 6px;
    border-radius: 6px;
    font-size: 12px;
    font-weight: 600;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
`;

const EditBtn = styled.button`
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: ${colors.neutralDark};
    border: none;
    cursor: pointer;
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${colors.night};
    transition: opacity 0.15s;

    &:hover { opacity: 0.7; }
`;

/* ─── Edit sheet ─── */

const SheetBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 24px;
`;

const SheetSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
`;

const SheetSectionTitle = styled.h4`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightLight};
    text-transform: uppercase;
    letter-spacing: 0.4px;
    margin: 0;
    display: flex;
    align-items: center;
    gap: 8px;
`;

const CommandBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
    border-radius: 6px;
    background: ${panelAccentAlpha(colors.occasionalPurple, '15')};
    color: ${panelAccent(colors.occasionalPurple)};
    text-transform: none;
    letter-spacing: 0;
`;

const Field = styled.div`
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

const FieldLabel = styled.label`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const FieldInput = styled.input`
    border: 1.5px solid ${colors.neutralDark};
    border-radius: 10px;
    padding: 13px 16px;
    font-size: 15px;
    color: ${colors.nightDark};
    background: ${({ disabled }) => disabled ? colors.neutralBackground : '#fff'};
    outline: none;
    width: 100%;
    box-sizing: border-box;
    transition: border-color 0.15s;

    &:focus:not(:disabled) {
        border-color: ${panelAccent(colors.occasionalPurple)};
    }

    &::placeholder {
        color: ${colors.nightLight};
    }

    &:disabled {
        cursor: not-allowed;
        color: ${colors.nightLight};
    }
`;

const ErrorMsg = styled.p`
    font-size: 13px;
    color: ${colors.danger};
    margin: 0;
`;

const SaveBtn = styled.button`
    padding: 15px;
    border-radius: 10px;
    background: ${({ disabled }) => disabled ? colors.neutralDark : panelAccent(colors.occasionalPurple)};
    color: ${({ disabled }) => disabled ? colors.nightLight : '#fff'};
    font-size: 15px;
    font-weight: 600;
    border: none;
    cursor: ${({ disabled }) => disabled ? 'not-allowed' : 'pointer'};
    transition: opacity 0.15s;

    &:hover:not(:disabled) { opacity: 0.9; }
`;

/* ─── Commands in sheet ─── */

const CommandList = styled.div`
    display: flex;
    flex-direction: column;
    border: 1px solid ${colors.neutralDark};
    border-radius: 12px;
    overflow: hidden;
`;

const CommandRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 13px 16px;
    gap: 12px;
    border-bottom: 1px solid ${colors.neutralDark};

    &:last-child {
        border-bottom: none;
    }
`;

const CommandInfo = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    flex-wrap: wrap;
    min-width: 0;
`;

const CommandId = styled.span`
    font-size: 13px;
    font-weight: 600;
    color: ${colors.nightDark};
    font-family: monospace;
`;

const CommandTable = styled.span`
    font-size: 13px;
    color: ${colors.night};
`;

const CommandDate = styled.span`
    font-size: 12px;
    color: ${colors.nightLight};
`;

const CommandMeta = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
`;

const CommandTotal = styled.span`
    font-size: 14px;
    font-weight: 600;
    color: ${colors.nightDark};
`;

const StatusBadge = styled.span`
    font-size: 11px;
    font-weight: 600;
    padding: 3px 8px;
    border-radius: 6px;
    background: ${({ $color }) => $color}18;
    color: ${({ $color }) => $color};
`;

const NoCommandsHint = styled.p`
    font-size: 13px;
    color: ${colors.nightLight};
    margin: 0;
`;
